/**
 * Recurring workshop series: the cadence vocabulary and the
 * occurrence generator. Shared so the create drawer previews exactly the dates
 * the API goes on to create — one generator, two readers.
 *
 * Every Date in this module is a venue-local wall clock encoded in the Date's
 * UTC components (2 PM is `14:00:00Z`). That is the encoding the API's write
 * path and `Workshop.localStartsAt` already use, so a series steps through
 * calendar dates without the engine's own time zone ever taking part. The API
 * derives each occurrence's absolute instant through the venue's zone after
 * generation, which is what keeps a 7 PM workshop at 7 PM across a DST change.
 */

export const WORKSHOP_CADENCES = ['weekly', 'biweekly', 'monthly_nth_weekday'] as const;
export type WorkshopCadence = (typeof WORKSHOP_CADENCES)[number];

/** A series must produce at least this many workshops — one is a one-off. */
export const MIN_SERIES_OCCURRENCES = 2;
/** The most workshops one series may create (a weekly year). */
export const MAX_SERIES_OCCURRENCES = 52;
/** How far past the first occurrence a series may reach, in days. */
export const MAX_SERIES_HORIZON_DAYS = 366;

/** 0 = Sunday … 6 = Saturday, matching `Date.getUTCDay()`. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Which weekday of the month a monthly series lands on: 1–4, or -1 for the last. */
export type NthWeekday = 1 | 2 | 3 | 4 | -1;

/** The end condition the operator states. Exactly one key is set. */
export type WorkshopSeriesEnd = { untilDate: string } | { occurrenceCount: number };

export type WorkshopRecurrence = { cadence: WorkshopCadence } & WorkshopSeriesEnd;

/** What a series is anchored to, derived from its first occurrence. */
export interface SeriesAnchor {
  weekday: Weekday;
  /** Set for the monthly cadence only. */
  nthWeekday: NthWeekday | null;
}

export const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const ORDINAL_LABELS: Record<NthWeekday, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
  [-1]: 'last',
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Parse a `YYYY-MM-DD` string to a wall-clock Date at midnight; null if malformed. */
export function parseCalendarDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return null;
  const roundTrips =
    date.getUTCFullYear() === Number(match[1]) &&
    date.getUTCMonth() === Number(match[2]) - 1 &&
    date.getUTCDate() === Number(match[3]);
  return roundTrips ? date : null;
}

/** The calendar date (`YYYY-MM-DD`) of a wall-clock Date. */
export function wallClockCalendarDate(wallClock: Date): string {
  return wallClock.toISOString().slice(0, 10);
}

/**
 * Which weekday a first occurrence falls on and, for a monthly series, which
 * one of the month it is. A fifth occurrence of a weekday (the 29th–31st) is
 * treated as "the last", since most months have no fifth.
 */
export function seriesAnchorFor(firstWallClock: Date, cadence: WorkshopCadence): SeriesAnchor {
  const weekday = firstWallClock.getUTCDay() as Weekday;
  if (cadence !== 'monthly_nth_weekday') return { weekday, nthWeekday: null };
  const ordinal = Math.ceil(firstWallClock.getUTCDate() / 7);
  return { weekday, nthWeekday: ordinal >= 5 ? -1 : (ordinal as NthWeekday) };
}

/** The day of `year`/`month` (0-based month) that is the nth `weekday`, or the last. */
function nthWeekdayOfMonth(year: number, month: number, anchor: SeriesAnchor): number {
  if (anchor.nthWeekday === -1) {
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    return lastDay.getUTCDate() - ((lastDay.getUTCDay() - anchor.weekday + 7) % 7);
  }
  const firstDay = new Date(Date.UTC(year, month, 1));
  const firstMatch = 1 + ((anchor.weekday - firstDay.getUTCDay() + 7) % 7);
  return firstMatch + 7 * ((anchor.nthWeekday ?? 1) - 1);
}

/** The occurrence `index` steps after the first, keeping the wall-clock time. */
function occurrenceAt(first: Date, index: number, cadence: WorkshopCadence, anchor: SeriesAnchor) {
  if (cadence === 'weekly') return new Date(first.getTime() + index * 7 * DAY_MS);
  if (cadence === 'biweekly') return new Date(first.getTime() + index * 14 * DAY_MS);
  const year = first.getUTCFullYear();
  const month = first.getUTCMonth() + index;
  const day = nthWeekdayOfMonth(year, month, anchor);
  return new Date(
    Date.UTC(
      year,
      month,
      day,
      first.getUTCHours(),
      first.getUTCMinutes(),
      first.getUTCSeconds(),
      first.getUTCMilliseconds(),
    ),
  );
}

/**
 * Every occurrence of a series, first one included, as wall-clock Dates.
 *
 * Stops at the operator's end condition, and in any case at
 * {@link MAX_SERIES_OCCURRENCES} occurrences or {@link MAX_SERIES_HORIZON_DAYS}
 * after the first, so a far-off "until" date cannot fan out without bound. An
 * unparseable `untilDate` yields just the first occurrence; the validators
 * upstream reject it with a proper message.
 */
export function generateSeriesOccurrences(
  firstWallClock: Date,
  recurrence: WorkshopRecurrence,
): Date[] {
  const anchor = seriesAnchorFor(firstWallClock, recurrence.cadence);
  const horizonEnd = firstWallClock.getTime() + MAX_SERIES_HORIZON_DAYS * DAY_MS;
  let stopBefore = horizonEnd + 1;
  let limit = MAX_SERIES_OCCURRENCES;
  if ('occurrenceCount' in recurrence) {
    limit = Math.min(recurrence.occurrenceCount, MAX_SERIES_OCCURRENCES);
  } else {
    const until = parseCalendarDate(recurrence.untilDate);
    if (!until) return [firstWallClock];
    stopBefore = Math.min(stopBefore, until.getTime() + DAY_MS);
  }

  const occurrences: Date[] = [];
  for (let index = 0; occurrences.length < limit; index += 1) {
    const next = occurrenceAt(firstWallClock, index, recurrence.cadence, anchor);
    if (next.getTime() >= stopBefore) break;
    occurrences.push(next);
  }
  return occurrences;
}

/**
 * Why a series cannot be created as stated, as the message to show the
 * operator, or null when it can. Shared so the drawer and the API refuse the
 * same inputs with the same words. Assumes `recurrence` is already well-formed
 * (one end condition, a parseable date), which the schemas on both sides check.
 */
export function seriesEndProblem(
  firstWallClock: Date,
  recurrence: WorkshopRecurrence,
): string | null {
  if ('untilDate' in recurrence) {
    const until = parseCalendarDate(recurrence.untilDate);
    if (!until) return 'Enter the end date as YYYY-MM-DD.';
    const firstDate = parseCalendarDate(wallClockCalendarDate(firstWallClock)) as Date;
    if (until.getTime() <= firstDate.getTime()) {
      return 'The end date must be after the first workshop.';
    }
    if (until.getTime() > firstDate.getTime() + MAX_SERIES_HORIZON_DAYS * DAY_MS) {
      return 'A series can run for at most a year.';
    }
    const anchor = seriesAnchorFor(firstWallClock, recurrence.cadence);
    const overflow = occurrenceAt(
      firstWallClock,
      MAX_SERIES_OCCURRENCES,
      recurrence.cadence,
      anchor,
    );
    if (overflow.getTime() < until.getTime() + DAY_MS) {
      return `A series can have at most ${MAX_SERIES_OCCURRENCES} workshops. Pick an earlier end date.`;
    }
  }
  if (generateSeriesOccurrences(firstWallClock, recurrence).length < MIN_SERIES_OCCURRENCES) {
    return 'This repeats only once before it ends. Pick a later end date, or create a one-off.';
  }
  return null;
}

/** "Weekly on Thursdays" / "Every 2 weeks on Thursdays" / "Monthly on the 2nd Thursday". */
export function cadenceLabel(cadence: WorkshopCadence, anchor: SeriesAnchor): string {
  const day = WEEKDAY_NAMES[anchor.weekday];
  if (cadence === 'weekly') return `Weekly on ${day}s`;
  if (cadence === 'biweekly') return `Every 2 weeks on ${day}s`;
  return `Monthly on the ${ORDINAL_LABELS[anchor.nthWeekday ?? 1]} ${day}`;
}

/** The cadence as a headline plus its weekday detail, for a KPI cell. */
export function cadenceSummary(
  cadence: WorkshopCadence,
  anchor: SeriesAnchor,
): { headline: string; detail: string } {
  const day = WEEKDAY_NAMES[anchor.weekday];
  if (cadence === 'weekly') return { headline: 'Weekly', detail: `${day}s` };
  if (cadence === 'biweekly') return { headline: 'Biweekly', detail: `${day}s` };
  return {
    headline: 'Monthly',
    detail: `${ORDINAL_LABELS[anchor.nthWeekday ?? 1]} ${day}`,
  };
}
